import { ReactNode, createContext, useCallback, useMemo } from 'react';

import { ThemeMode, ThemeDirection, ThemeColor, SettingsContextProps } from '../@types/settings';
import useLocalStorage from '../hooks/useLocalStorage';
import palette from '../theme/palette';

const initialState: SettingsContextProps = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeColor: 'default',
  themeStretch: false,
  onChangeMode: () => Object,
  onChangeDirection: () => Object,
  onChangeColor: () => Object,
  onToggleStretch: () => Object,
  setColor: {
    name: 'default',
    ...palette.light.primary,
  },
  colorOption: [],
};

const SettingsContext = createContext(initialState);

export interface ISettingsProviderProps {
  readonly children: ReactNode;
}
function SettingsProvider({ children }: ISettingsProviderProps): JSX.Element {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: 'light' as ThemeMode,
    themeDirection: 'ltr' as ThemeDirection,
    themeColor: 'default' as ThemeColor,
    themeStretch: false,
  });

  const defaultPalette = palette.light.primary;

  const PRIMARY_COLOR = useMemo(
    () => [
      // DEFAULT
      {
        name: 'default',
        ...defaultPalette,
      },
      // PURPLE
      {
        name: 'purple',
        lighter: '#EBD6FD',
        light: '#B985F4',
        main: '#7635dc',
        dark: '#431A9E',
        darker: '#200A69',
        contrastText: '#fff',
      },
      // CYAN
      {
        name: 'cyan',
        lighter: '#D1FFFC',
        light: '#76F2FF',
        main: '#1CCAFF',
        dark: '#0E77B7',
        darker: '#053D7A',
        contrastText: palette.light.grey[800],
      },
      // BLUE
      {
        name: 'blue',
        lighter: '#CCDFFF',
        light: '#6697FF',
        main: '#0045FF',
        dark: '#0027B7',
        darker: '#00137A',
        contrastText: '#fff',
      },
      // ORANGE
      {
        name: 'orange',
        lighter: '#FEF4D4',
        light: '#FED680',
        main: '#fda92d',
        dark: '#B66816',
        darker: '#793908',
        contrastText: palette.light.grey[800],
      },
      // RED
      {
        name: 'red',
        lighter: '#FFE3D5',
        light: '#FFC1AC',
        main: '#FF3030',
        dark: '#B71833',
        darker: '#7A0930',
        contrastText: '#fff',
      },
    ],
    [defaultPalette],
  );

  const SetColor = useCallback(
    (themeColor: ThemeColor): (typeof PRIMARY_COLOR)[0] => {
      let color;
      const DEFAULT = PRIMARY_COLOR[0];
      const PURPLE = PRIMARY_COLOR[1];
      const CYAN = PRIMARY_COLOR[2];
      const BLUE = PRIMARY_COLOR[3];
      const ORANGE = PRIMARY_COLOR[4];
      const RED = PRIMARY_COLOR[5];

      switch (themeColor) {
        case 'purple':
          color = PURPLE;
          break;

        case 'cyan':
          color = CYAN;
          break;

        case 'blue':
          color = BLUE;
          break;

        case 'orange':
          color = ORANGE;
          break;

        case 'red':
          color = RED;
          break;

        default:
          color = DEFAULT;
      }

      return color;
    },
    [PRIMARY_COLOR],
  );

  const onChangeMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSettings({
        ...settings,
        themeMode: (event.target as HTMLInputElement).value as ThemeMode,
      });
    },
    [setSettings, settings],
  );

  const onChangeDirection = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSettings({
        ...settings,
        themeDirection: (event.target as HTMLInputElement).value as ThemeDirection,
      });
    },
    [setSettings, settings],
  );

  const onChangeColor = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSettings({
        ...settings,
        themeColor: (event.target as HTMLInputElement).value as ThemeColor,
      });
    },
    [setSettings, settings],
  );

  const onToggleStretch = useCallback((): void => {
    setSettings({
      ...settings,
      themeStretch: !settings.themeStretch,
    });
  }, [setSettings, settings]);

  const settingsContextValue = useMemo(
    () => ({
      ...settings,
      onChangeMode,
      onChangeDirection,
      onChangeColor,
      onToggleStretch,
      setColor: SetColor(settings.themeColor),
      colorOption: PRIMARY_COLOR.map((color) => ({
        name: color.name,
        value: color.main,
      })),
    }),
    [PRIMARY_COLOR, SetColor, onChangeColor, onChangeDirection, onChangeMode, settings, onToggleStretch],
  );

  return <SettingsContext.Provider value={settingsContextValue}>{children}</SettingsContext.Provider>;
}

export { SettingsProvider, SettingsContext };
